/**
 * Browser-side transforms mirroring whiteglove-cli `src/core/scrubber.py`
 * (no SQL — operate on row objects and values). Used by the uploader preview
 * and later POST payload.
 */

/** @param {string | null | undefined} s */
export function stripLineBreaks(s) {
  if (s == null) return s
  return String(s).replace(/[\n\r]+/g, "")
}

/** Non-alphanumeric stripped, lowercased — matches scrubber PO Box check. */
export function isPoboxAddress(s) {
  if (s == null) return false
  const t = String(s)
    .replace(/[^A-Za-z]/g, "")
    .toLowerCase()
  return t.includes("pobox")
}

function nullifEmpty(s) {
  if (s == null) return null
  const t = String(s).trim()
  return t === "" ? null : t
}

/**
 * Interpret a 2-digit year as a birth year: never in the future; prefer 20xx when still a plausible DOB
 * (e.g. 1/1/47 → 1947, not 2047; recent years like /24 → 2024 when valid).
 * @param {number} yy 0–99 (or full year mod 100)
 * @param {number} month 1–12
 * @param {number} day 1–31
 */
export function fullYearFromTwoDigitDob(yy, month, day) {
  const n = ((yy % 100) + 100) % 100
  const now = new Date()
  const y20 = 2000 + n
  const y19 = 1900 + n

  const validBirth = (y) => {
    const d = new Date(y, month - 1, day)
    if (Number.isNaN(d.getTime())) return false
    if (d > now) return false
    const ageMs = now.getTime() - d.getTime()
    const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000)
    if (ageYears > 120) return false
    return true
  }

  if (validBirth(y20)) return y20
  if (validBirth(y19)) return y19
  return y20 <= now.getFullYear() ? y20 : y19
}

/**
 * coalesce_mobile_home: first non-empty of mobile, home.
 * @returns {{ phone: string | null, type: 'mobile' | 'home' | null }}
 */
export function coalesceMobileHome(mobile, home) {
  const m = nullifEmpty(mobile)
  const h = nullifEmpty(home)
  if (m != null) return { phone: m, type: "mobile" }
  if (h != null) return { phone: h, type: "home" }
  return { phone: null, type: null }
}

/**
 * Same semantics as `run_phone_strategy_coalesce_mobile_home_other` in scrubber.py
 * (three phone slots + type columns + two secondary + third).
 * @returns {{
 *   scrub_phone: string | null, scrub_phone_type: string | null,
 *   scrub_phone_2: string | null, scrub_phone_type_2: string | null,
 *   scrub_phone_3: string | null, scrub_phone_type_3: string | null
 * }}
 */
export function coalesceMobileHomeOther(mobile, home, other) {
  const m = nullifEmpty(mobile)
  const h = nullifEmpty(home)
  const o = nullifEmpty(other)
  const scrub_phone = m ?? h ?? o

  let scrub_phone_type = null
  if (scrub_phone == null) scrub_phone_type = null
  else if (scrub_phone === m) scrub_phone_type = "mobile"
  else if (scrub_phone === h) scrub_phone_type = "home"
  else if (scrub_phone === o) scrub_phone_type = "other"

  const scrub_phone_2 = h ?? o
  let scrub_phone_type_2 = null
  if (scrub_phone_2 == null) scrub_phone_type_2 = null
  else if (scrub_phone_2 === h) scrub_phone_type_2 = "home"
  else if (scrub_phone_2 === o) scrub_phone_type_2 = "other"

  const scrub_phone_3 = o
  const scrub_phone_type_3 = o != null ? "o" : null

  return {
    scrub_phone,
    scrub_phone_type,
    scrub_phone_2,
    scrub_phone_type_2,
    scrub_phone_3,
    scrub_phone_type_3
  }
}

/**
 * pad_year: m/d/yy or similar — pad year to 19yy in third segment.
 * @param {string} delimiter  e.g. "/"
 */
export function dobPadYear(value, delimiter) {
  if (value == null) return null
  const t = String(value).trim()
  if (!t) return null
  const parts = t.split(delimiter)
  if (parts.length !== 3) return t
  const a = parts[0].trim()
  const b = parts[1].trim()
  let y = parts[2].trim()
  if (y.length <= 2) {
    const yi = parseInt(y, 10)
    const mi = parseInt(a, 10)
    const di = parseInt(b, 10)
    if (!Number.isNaN(yi) && !Number.isNaN(mi) && !Number.isNaN(di)) {
      y = String(fullYearFromTwoDigitDob(yi, mi, di))
    } else {
      y = `19${y}`
    }
  }
  return `${a}${delimiter}${b}${delimiter}${y}`
}

/**
 * @returns {string | null}  YYYY-MM-DD if parseable, else null
 */
export function dobNormalize(value) {
  if (value == null) return null
  const t = String(value).trim()
  if (!t) return null

  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    const dIso = new Date(t)
    if (!Number.isNaN(dIso.getTime())) return dIso.toISOString().slice(0, 10)
  }

  const m = t.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/)
  if (m) {
    const mm = parseInt(m[1], 10)
    const dd = parseInt(m[2], 10)
    let yy = parseInt(m[3], 10)
    if (m[3].length === 2) yy = fullYearFromTwoDigitDob(yy, mm, dd)
    const d2 = new Date(yy, mm - 1, dd)
    if (!Number.isNaN(d2.getTime())) return d2.toISOString().slice(0, 10)
    return t
  }

  const d = new Date(t)
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  return t
}

/**
 * @param {'last_comma_first' | 'first_space_last'} strategy
 * @returns {{ first: string, last: string }}
 */
export function splitFullName(value, delimiter, strategy) {
  const t = value == null ? "" : String(value)
  if (!t.trim()) return { first: "", last: "" }
  const d = delimiter || " "
  const isFirstSpaceLast = strategy === "first_space_last"
  const firstPos = isFirstSpaceLast ? 1 : 2
  const lastPos = isFirstSpaceLast ? 2 : 1
  const getPart = (n) => {
    const s = t.split(d)
    return (s[n - 1] || "").trim()
  }
  return {
    first: getPart(firstPos),
    last: getPart(lastPos)
  }
}

/**
 * Parse first Geocode API result (same field picks as scrubber.py).
 * @param {object} result0  gaddress_json['results'][0]
 * @returns {{ line1: string, city: string | null, state: string | null, zip: string | null, raw: object }}
 */
export function parseGeocodeResult(result0) {
  if (!result0 || !result0.address_components) {
    return { line1: "", city: null, state: null, zipCode: null, raw: result0 }
  }
  let streetNumber = null
  let route = null
  let subpremise = null
  let city = null
  let state = null
  let zip = null
  for (const comp of result0.address_components) {
    const types = comp.types || []
    if (types.includes("street_number")) streetNumber = comp.short_name
    else if (types.includes("route")) route = String(comp.short_name || "").replace(/'/g, "")
    else if (types.includes("subpremise")) subpremise = comp.short_name
    else if (types.includes("locality")) city = comp.short_name
    else if (types.includes("administrative_area_level_1")) state = comp.short_name
    else if (types.includes("postal_code")) zip = comp.short_name
  }
  const lineParts = [streetNumber, route].filter(Boolean)
  let line1 = lineParts.length ? lineParts.join(" ") : ""
  if (subpremise) line1 = `${line1} ${subpremise}`.trim()
  return { line1, city, state, zipCode: zip, raw: result0 }
}

/**
 * @param {string} address
 * @param {string} apiKey
 * @returns {Promise<object|null>}  Full JSON; null on failure
 */
export async function fetchGeocodeJson(address, apiKey) {
  if (!apiKey || !String(address).trim()) return null
  const u = new URL("https://maps.googleapis.com/maps/api/geocode/json")
  u.searchParams.set("address", String(address).trim())
  u.searchParams.set("key", apiKey)
  const r = await fetch(u.toString())
  if (!r.ok) return null
  return r.json()
}

/** Filter patient rows: drop when null column (header) is blank. */
export function shouldKeepRow(patientRow, nullColumnHeader) {
  if (nullColumnHeader == null) return true
  const h = nullColumnHeader
  const v = patientRow[h]
  if (v == null) return false
  if (String(v).trim() === "") return false
  return true
}
